import React, { useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { logout } from '@/lib/api/auth';
import { readSubscriptionGateState } from '@/lib/subscriptionGate';

const formatPlanLabel = (planCode: string | null | undefined): string => {
  if (!planCode) {
    return 'CalorieScience';
  }

  return planCode
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

interface SubscriptionRequiredModalProps {
  open: boolean;
}

export const SubscriptionRequiredModal: React.FC<SubscriptionRequiredModalProps> = ({ open }) => {
  const gateState = readSubscriptionGateState();
  const subscription = gateState?.subscription;

  const trialEndsLabel = useMemo(() => {
    if (!subscription?.endsAt) {
      return null;
    }

    return format(new Date(subscription.endsAt), 'd MMM yyyy');
  }, [subscription?.endsAt]);

  const isDismissingRef = useRef(false);

  const handleDismiss = () => {
    if (isDismissingRef.current) {
      return;
    }

    isDismissingRef.current = true;
    logout();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleDismiss();
        }
      }}
    >
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(event) => {
          event.preventDefault();
          handleDismiss();
        }}
        onEscapeKeyDown={handleDismiss}
      >
        <DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0f7fa4]">Subscription</p>
          <DialogTitle className="text-2xl font-semibold text-slate-800">
            Subscription required to continue
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-slate-600">
            {gateState?.message
              || 'Your account needs an active CalorieScience subscription before you can access the platform.'}
          </DialogDescription>
        </DialogHeader>

        {subscription ? (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium text-slate-500">Plan</span>
              <span className="font-semibold text-slate-800">{formatPlanLabel(subscription.planCode)}</span>
            </div>
            {subscription.status ? (
              <div className="flex items-center justify-between gap-4">
                <span className="font-medium text-slate-500">Status</span>
                <span className="capitalize text-slate-800">{subscription.status}</span>
              </div>
            ) : null}
            {trialEndsLabel ? (
              <div className="flex items-center justify-between gap-4">
                <span className="font-medium text-slate-500">Trial ends</span>
                <span className="text-slate-800">{trialEndsLabel}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {gateState?.supportEmail ? (
          <p className="text-sm text-slate-600">
            Need help? Contact{' '}
            <a
              href={`mailto:${gateState.supportEmail}`}
              className="font-medium text-[#0f7fa4] underline"
            >
              {gateState.supportEmail}
            </a>
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            onClick={handleDismiss}
            className="w-full !bg-[#20AEDC] !text-white hover:!bg-[#1a99c0] sm:w-auto"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
